<template>
  <div
    v-if="modalTrigger"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
  >
    <div
      class="bg-white w-full max-w-xl mx-4 p-6 rounded-xl shadow-lg relative overflow-hidden"
    >
      <div class="flex items-center pt-4 pb-5">
        <button
          @click="handleClose()"
          class="absolute top-3 right-3 text-gray-500 hover:text-gray-700 text-2xl font-bold"
        >
          &times;
        </button>
        <h2 class="text-xl font-semibold text-secondary">
          {{
            ifCurrentMode === "add"
              ? "Request For Payment"
              : "Releasing Payment Request"
          }}
        </h2>
      </div>

      <hr class="pb-5" />
      <form
        @submit.prevent="createConfirmationHandler()"
        v-if="ifCurrentMode === 'add'"
      >
        <div class="space-y-2">
          <div class="flex gap-1 flex-col relative">
            <label class="block text-sm text-gray-700 pb-1"
              >Transaction Number</label
            >
            <input
              required
              v-model="transactionnumber"
              @input="filterSuggestions"
              @focus="showSuggestions = true"
              @blur="hideWithDelay"
              type="text"
              class="w-full border shadow-elegant rounded-md px-4 py-2.5 text-sm,"
              placeholder="Enter Transaction Number"
            />
            <ul
              v-if="showSuggestions && filteredSuggestions.length"
              class="absolute top-full left-0 w-full bg-white border border-gray-300 rounded shadow z-10 max-h-48 overflow-auto"
            >
              <li
                v-for="(item, index) in filteredSuggestions.slice(0, 25)"
                :key="index"
                @mousedown.prevent="selectReference(item)"
                class="px-4 py-2 text-sm cursor-pointer hover:bg-blue-100"
              >
                <div class="flex flex-col gap-1">
                  <span class="text-sm text-secondary font-semibold">{{
                    item.transactionnumber
                  }}</span>
                  <span class="text-xs text-primary font-medium">{{
                    item.membername
                  }}</span>
                </div>
              </li>
            </ul>
          </div>
          <div class="flex gap-1 flex-col">
            <label class="block text-sm text-gray-700 pb-1">Payee</label>
            <input
              :disabled="!filteredSuggestions.length"
              required
              v-model="formData.membername"
              type="text"
              class="w-full border shadow-elegant rounded-md px-4 py-2.5 text-sm"
              placeholder="Name of Payee"
            />
          </div>
          <div class="flex gap-1 flex-col" v-if="false">
            <label class="block text-sm text-gray-700 pb-1"
              >Requested Amount</label
            >
            <input
              :disabled="!filteredSuggestions.length"
              required
              v-model="formData.requestedamount"
              type="number"
              step="any"
              class="w-full border shadow-elegant rounded-md px-4 py-2.5 text-sm"
              placeholder="Enter Request Amount"
            />
          </div>
          <div class="flex gap-1 flex-col">
            <label class="block text-sm text-gray-700 pb-1"
              >Guaranteed Amount
            </label>
            <input
              :disabled="!filteredSuggestions.length"
              required
              v-model="formData.reimbursableamountFormat"
              @blur="paymentMethodHandler"
              @focus="
                formData.reimbursableamountFormat =
                  formData.reimbursableamount?.toString() || ''
              "
              type="text"
              class="w-full border shadow-elegant rounded-md px-4 py-2.5 text-sm"
              placeholder="Enter Approve Reimbursement Amount"
            />
          </div>
          <div v-if="ifRenderPaymentMethod" class="space-y-2">
            <div class="flex gap-1 flex-col">
              <label class="block text-sm text-gray-700 pb-1"
                >{{ ifBankDeposit ? "Bank Name" : "E-wallet" }}
              </label>
              <select
                required
                class="w-full border shadow-elegant rounded-md px-4 py-2.5 text-sm"
                v-model="formData.paymentprovider"
              >
                <option disabled value="">--Please select--</option>
                <option
                  v-for="(item, index) in ifBankDeposit
                    ? bankItem
                    : ewalletItem"
                  :key="index"
                  :value="item"
                >
                  {{ item }}
                </option>
              </select>
            </div>

            <div class="flex gap-1 flex-col">
              <label class="block text-sm text-gray-700 pb-1"
                >Account Number
              </label>
              <input
                required
                v-model="formData.paymentaccount"
                type="text"
                class="w-full border shadow-elegant rounded-md px-4 py-2.5 text-sm"
                :placeholder="`Enter Account Number`"
              />
            </div>

            <div class="flex gap-1 flex-col" v-if="!ifBankDeposit">
              <label class="block text-sm text-gray-700 pb-1"
                >Proof of Payment</label
              >
            </div>
            <div class="relative" v-if="!ifBankDeposit">
              <label
                for="file-upload"
                class="flex flex-col items-center justify-center w-full h-36 px-4 transition bg-white border-2 border-dashed rounded-2xl cursor-pointer border-gray-300 hover:border-blue-400 focus:outline-none"
              >
                <div
                  class="flex flex-col items-center justify-center space-y-2"
                >
                  <svg
                    class="w-8 h-8 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M7 16V4m0 0l-3 3m3-3l3 3M17 8v8a4 4 0 01-4 4H7a4 4 0 01-4-4V8a4 4 0 014-4h3"
                    />
                  </svg>
                  <p class="text-sm text-gray-600 text-center">
                    <span class="font-medium text-blue-600"
                      >Please Upload Proof of Payment</span
                    >
                    or drag and drop<br />
                    PNG, JPG up to 10MB
                  </p>
                </div>
                <input
                  id="file-upload"
                  type="file"
                  accept="image/*"
                  class="hidden"
                  required
                  @change="handleImageUpload"
                />
              </label>

              <div v-if="selectedImage" class="mt-4 text-center">
                <p class="text-sm text-gray-500 mb-2">Preview:</p>
                <img
                  :src="selectedImage"
                  alt="Preview"
                  class="mx-auto w-72 h-48 object-cover shadow-md border"
                />
              </div>
            </div>
          </div>
          <div class="pt-4">
            <button
              :disabled="!filteredSuggestions.length"
              type="submit"
              class="w-full bg-orange-400 text-white font-semibold py-3 rounded-md hover:bg-primary transition-colors"
            >
              Submit
            </button>
          </div>
        </div>
      </form>

      <form @submit.prevent="updateConfirmationHandler()" v-else>
        <div class="space-y-2">
          <div class="flex gap-1 flex-col">
            <label class="block text-sm text-gray-700 pb-1">Payee</label>
            <input
              :disabled="ifCurrentMode === 'edit'"
              required
              v-model="formData.paymentpayee"
              type="text"
              class="w-full border shadow-elegant rounded-md px-4 py-2.5 text-sm"
              placeholder="Name of Payee"
            />
          </div>
          <div class="flex gap-1 flex-col">
            <label class="block text-sm text-gray-700 pb-1"
              >Proof of Payment</label
            >
          </div>
          <div class="relative">
            <label
              for="file-upload"
              class="flex flex-col items-center justify-center w-full h-36 px-4 transition bg-white border-2 border-dashed rounded-2xl cursor-pointer border-gray-300 hover:border-blue-400 focus:outline-none"
            >
              <div class="flex flex-col items-center justify-center space-y-2">
                <svg
                  class="w-8 h-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M7 16V4m0 0l-3 3m3-3l3 3M17 8v8a4 4 0 01-4 4H7a4 4 0 01-4-4V8a4 4 0 014-4h3"
                  />
                </svg>
                <p class="text-sm text-gray-600 text-center">
                  <span class="font-medium text-blue-600"
                    >Please Upload Proof of Payment</span
                  >
                  or drag and drop<br />
                  PNG, JPG up to 10MB
                </p>
              </div>
              <input
                id="file-upload"
                type="file"
                accept="image/*"
                class="hidden"
                required
                @change="handleImageUpload"
              />
            </label>

            <div v-if="selectedImage" class="mt-4 text-center">
              <p class="text-sm text-gray-500 mb-2">Preview:</p>
              <img
                :src="selectedImage"
                alt="Preview"
                class="mx-auto w-72 h-48 object-cover shadow-md border"
              />
            </div>
          </div>
          <div class="pt-4">
            <button
              type="submit"
              class="w-full bg-orange-400 text-white font-semibold py-3 rounded-md hover:bg-primary transition-colors"
            >
              Update
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, reactive, watch, inject } from "vue";

export default {
  props: {
    modalTrigger: {
      type: Boolean,
      required: true,
    },
    ifCurrentMode: {
      type: String,
    },
    renderDataItems: {
      type: Object,
    },
  },
  emits: ["close"],
  setup(props, { emit }) {
    const Swal = inject("$swal");
    const axios = inject("$axios");
    const moment = inject("$moment");
    const isSubmitting = ref(false);
    const originalMembername = ref();
    const formData = reactive({
      transactionnumber: null,
      membername: null,
      requestedamount: 0,
      reimbursableamount: 0,
      reimbursableamountFormat: "",
      paymentprovider: "",
      paymentaccount: "",
      paymentstatus: "pending",
      paymentdate: null,
      paymentreference: null,
    });
    const ewalletItem = ["Gcash", "Paymaya", "Bank Transfer"];
    const bankItem = [
      "BDO Unibank, Inc. (BDO)",
      "Bank of the Philippine Islands (BPI)",
      "Land Bank of the Philippines (Landbank)",
      "Metropolitan Bank and Trust Company (Metrobank)",
      "Philippine National Bank (PNB)",
      "China Banking Corporation (Chinabank)",
      "Security Bank Corporation",
      "Union Bank of the Philippines (UnionBank)",
      "Rizal Commercial Banking Corporation (RCBC)",
      "EastWest Banking Corporation (EastWest Bank)",
      "Asia United Bank Corporation (AUB)",
      "United Coconut Planters Bank (UCPB – now merged with Landbank)",
      "Development Bank of the Philippines (DBP)",
    ];
    const filteredSuggestions = ref([]);
    const showSuggestions = ref(false);
    let debounceTimeout = null;
    const ifBankDeposit = ref(false);
    const ifRenderPaymentMethod = ref(false);
    const transactionnumber = ref(formData.transactionnumber || "");
    const selectedImage = ref(null);
    const selectedFile = ref(null);

    watch(
      () => props.modalTrigger,
      (newVal) => {
        if (newVal) {
          clearHandler();
          if (props.ifCurrentMode === "edit") {
            Object.assign(formData, props.renderDataItems);
          }
        }
      }
    );
    const handleClose = () => {
      emit("close", false);
    };

    const createConfirmationHandler = () => {
      Swal.fire({
        title: "Confirmation",
        text: "Are you sure you want save this request for payment?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      }).then((result) => {
        if (result.isConfirmed) {
          createProcessingHandler();
        }
      });
    };
    const createProcessingHandler = async () => {
      try {
        isSubmitting.value = true;

        const { data } = await axios.put(
          `/portal/application/reimbursement/${formData.transactionnumber}`,
          {
            paymentstatus: "Pending",
            requestedamount: 0.0,
            reimbursableamount: formData.reimbursableamount,
            paymentpayee: formData.membername,
            paymentprovider: formData.paymentprovider,
            paymentaccount: formData.paymentaccount,
            membername: originalMembername.value,
            paymentdate: moment().format("YYYY-MM-DD"),
            graceperiod: 0,
          }
        );
        if (data?.status) {
          if (!ifBankDeposit.value) {
            updateProcessingHandler();
          } else {
            await Swal.fire({
              icon: "success",
              title: "Successfully Process",
              html: `Payment request has been saved.`,
              confirmButtonText: "Ok",
            });

            emit("close");
            emit("success", true);
            clearHandler();
          }
        } else {
          await Swal.fire({
            icon: "error",
            title: "Oops!",
            text:
              data?.message || "Something went wrong. Please try again later.",
          });
        }
      } catch (err) {
        console.error("Form submission error:", err);

        await Swal.fire({
          icon: "error",
          title: "Submission Failed",
          text:
            err.response?.data?.message ||
            "Unable to process your request at the moment. Please try again.",
        });
      } finally {
        isSubmitting.value = false;
      }
    };
    const updateConfirmationHandler = () => {
      Swal.fire({
        title: "Are you sure?",
        text: "You want to tag this Release Payment?",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      }).then((result) => {
        if (result.isConfirmed) {
          updateProcessingHandler();
        }
      });
    };
    const updateProcessingHandler = async () => {
      try {
        isSubmitting.value = true;
        let filename;

        if (selectedFile.value) {
          const uploadForm = new FormData();
          uploadForm.append("file", selectedFile.value);

          let result = await axios.post(
            "/portal/application/reimbursement/payment/attachment",
            uploadForm,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );
          filename = result.data.filename;
        }

        const { data } = await axios.put(
          `/portal/application/reimbursement/payment/${formData.transactionnumber}`,
          {
            paymentreference: filename,
            paymentstatus: "Completed",
            membername: !originalMembername.value
              ? formData.membername
              : originalMembername.value,
            graceperiod: 0,
          }
        );

        if (data?.status) {
          await Swal.fire({
            icon: "success",
            title: "Successfully Completed",
            html: `Payment request has been released.`,
            confirmButtonText: "Ok",
          });

          emit("close");
          emit("success", true);
          clearHandler();
        } else {
          await Swal.fire({
            icon: "error",
            title: "Oops!",
            text:
              data?.message || "Something went wrong. Please try again later.",
          });
        }
      } catch (err) {
        console.error("Form submission error:", err);

        await Swal.fire({
          icon: "error",
          title: "Submission Failed",
          text:
            err.response?.data?.message ||
            "Unable to process your request at the moment. Please try again.",
        });
      } finally {
        isSubmitting.value = false;
      }
    };

    const filterSuggestions = () => {
      clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(async () => {
        const search = transactionnumber.value.trim();
        if (!search) {
          filteredSuggestions.value = [];
          return;
        }
        try {
          const { data } = await axios.get(
            `/portal/application/reimbursement/reference`,
            {
              params: {
                search: transactionnumber.value,
              },
            }
          );

          showSuggestions.value = true;
          filteredSuggestions.value = data;
          console.log(data);
        } catch (error) {
          console.error("Error fetching providers:", error);
          filteredSuggestions.value = [];
        }
      }, 300); // 300ms debounce
    };

    const selectTransactionnumber = (value) => {
      transactionnumber.value = value;
      formData.transactionnumber = value;
    };

    const hideWithDelay = () => {
      setTimeout(() => {
        showSuggestions.value = false;
      }, 150);
    };

    watch(transactionnumber, (val) => {
      formData.transactionnumber = val;
    });
    const selectReference = (item) => {
      console.log(item);
      transactionnumber.value = item.transactionnumber;
      formData.transactionnumber = item.transactionnumber;
      formData.membername = item.membername;
      originalMembername.value = item.membername;
      showSuggestions.value = false;
    };
    const paymentMethodHandler = () => {
      let num = parseFloat(formData.reimbursableamountFormat.replace(/,/g, ""));
      console.log(num);
      if (!isNaN(num)) {
        formData.reimbursableamount = num;
        formData.reimbursableamountFormat = num.toLocaleString("en-US"); // Add commas

        ifRenderPaymentMethod.value =
          formData.reimbursableamount > 1 ? true : false;
        const amount = Number(formData.reimbursableamount) || 1;
        const SETUP_AMOUNT = 5000;
        ifBankDeposit.value = amount >= SETUP_AMOUNT;
      }
    };

    const clearHandler = () => {
      selectedImage.value = null;
      selectedFile.value = null;
      transactionnumber.value = "";
      filteredSuggestions.value = [];
      ifBankDeposit.value = false;
      ifRenderPaymentMethod.value = false;
      Object.keys(formData).forEach((key) => {
        if (Array.isArray(formData[key])) {
          formData[key] = [];
        } else {
          formData[key] = null;
        }
      });
      originalMembername.value = null;
    };

    const handleImageUpload = (event) => {
      const file = event.target.files[0];
      if (file && file.type.startsWith("image/")) {
        selectedFile.value = file;
        selectedImage.value = URL.createObjectURL(file);
      } else {
        selectedFile.value = null;
        selectedImage.value = null;
      }
    };

    return {
      formData,
      transactionnumber,
      handleClose,
      createConfirmationHandler,
      createProcessingHandler,
      isSubmitting,
      filterSuggestions,
      filteredSuggestions,
      showSuggestions,
      debounceTimeout,
      selectTransactionnumber,
      hideWithDelay,
      selectReference,
      paymentMethodHandler,
      ifBankDeposit,
      ifRenderPaymentMethod,
      ewalletItem,
      bankItem,
      clearHandler,
      updateConfirmationHandler,
      updateProcessingHandler,
      selectedImage,
      selectedFile,
      handleImageUpload,
      originalMembername,
    };
  },
};
</script>
